import { FC, useState } from 'react'

import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'

import {
  useGetAllContractsQuery,
  useDeleteContractMutation,
} from 'src/Redux/api/contractsApi.ts'
import { useGetAllUsersQuery } from 'src/Redux/api/usersApi.ts'
import { Role } from 'src/Redux/api/authApi.ts'
import { PageTitle } from 'src/Components/PageTitle/index.tsx'
import { ContractsTable } from 'src/Pages/ContractsPage/ContractsTable.tsx'
import { SearchBlock } from 'src/Pages/ContractsPage/SearchBlock.tsx'
import { device } from 'src/Helpers/constants'
import { TableHeader } from 'src/Pages/ContractsPage/TableHeader.tsx'
import { Modal } from 'src/Components/Modal'
import { Button } from 'src/Components/Button'
import { RootState } from 'src/Redux'

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 50px;
  position: static;

  @media ${device.tabletMax} {
    gap: 20px;
    width: 100%;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
  position: relative;
  height: 100%;
  overflow: hidden;
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
`

const ModalText = styled.p`
  font-size: 16px;
  text-align: center;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`

export const ContractsPage: FC = () => {
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchValue, 300)
  const [contractToArchive, setContractToArchive] = useState<number | null>(
    null,
  )
  const [
    isArchiveModalOpened,
    { open: openArchiveModal, close: closeArchiveModal },
  ] = useDisclosure(false)

  const { data: contractsResponse, isLoading } = useGetAllContractsQuery({
    limit: 20,
    search: debouncedSearch || undefined,
  })
  const { data: usersResponse } = useGetAllUsersQuery()
  const [deleteContract] = useDeleteContractMutation()
  const currentUser = useSelector(
    (state: RootState) => state.authentication.user,
  )

  const isAdmin = currentUser?.role === Role.ADMIN

  const onLoadMore = (): void => {}

  const onArchiveContract = (id: number): void => {
    setContractToArchive(id)
    openArchiveModal()
  }

  const cancelArchive = (): void => {
    closeArchiveModal()
    setContractToArchive(null)
  }

  const confirmArchive = (): void => {
    if (contractToArchive === null) return

    deleteContract(contractToArchive)
      .unwrap()
      .then(() => {
        notifications.show({
          title: 'Договор отправлен в архив',
          message: '',
          position: 'top-right',
        })
        closeArchiveModal()
        setContractToArchive(null)
      })
      .catch(() => {
        notifications.show({
          title: 'Ошибка при архивировании договора',
          message: '',
          position: 'top-right',
          color: 'red',
        })
        closeArchiveModal()
        setContractToArchive(null)
      })
  }

  return (
    <Container>
      <TitleWrapper>
        <PageTitle>Договоры</PageTitle>

        <SearchBlock value={searchValue} onChange={setSearchValue} />
      </TitleWrapper>
      <TableHeader />
      <ContractsTable
        contracts={contractsResponse?.data}
        isLoading={isLoading}
        onLoadMore={onLoadMore}
        users={usersResponse?.data || []}
        currentUserId={currentUser?.id}
        canArchive={isAdmin}
        onArchive={onArchiveContract}
      />

      <Modal
        opened={isArchiveModalOpened}
        onClose={cancelArchive}
        title='Подтверждение архивирования'
        centered
      >
        <ModalContent>
          <ModalText>
            Вы уверены, что хотите отправить этот договор в архив?
          </ModalText>
          <ButtonGroup>
            <Button onClick={cancelArchive}>Отмена</Button>
            <Button
              onClick={confirmArchive}
              data-testid='confirm-archive-contract'
            >
              В архив
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>
    </Container>
  )
}
